'use client'
import { PortableText, PortableTextProps } from '@portabletext/react'
import { OrMapTile, OrSecondaryNavbarBdp } from '@studio/atomic-ui'

import { googleMapsApiKey } from '../../env'
import { SanityAtLink } from '../../services/sanity/types'
import { ContentBlock, SanityAssetList } from './content-block'
import { mapImage } from '../../services/sanity/client'
import { MouseEvent, useEffect, useRef } from 'react'
import { trackNavigationMenuClick } from '../../utils/analytics'
import { ObserverRefsType } from '../../types/observerRef'

export interface TmVisitorsGuideProps {
  heading: PortableTextProps['value']
  pageLinks: SanityAtLink[]
  location: {
    heading: PortableTextProps['value']
    addressInfo: PortableTextProps['value']
    mapTile: {
      id: string
      address: string
      details: SanityAssetList
    }
  }
  content: Array<ContentBlock>
}

export const TmVisitorsGuide = ({ heading, pageLinks, location, content }: TmVisitorsGuideProps) => {
  const observerRefs: ObserverRefsType = useRef({})

  useEffect(() => {
    if (location) observerRefs.current[location.mapTile.id] = null
    content.forEach((block) => {
      if (block.id) observerRefs.current[block.id] = null
    })
  }, [content, location])

  return (
    <div className="py-10 slg:pt-16 lg:px-[10.4375rem] lg:max-w-screen-lg lg:mx-auto">
      <section className="pb-8 px-4 lg:px-0">
        <PortableText
          value={heading}
          components={{
            block: {
              h1: ({ children }) => <h1 className="font-heading font-medium text-40 leading-1.3 pb-5">{children}</h1>,
              normal: ({ children }) => (
                <p className="text-neutrals-700 text-14 lg:text-16 max-w-[34rem]">{children}</p>
              ),
            },
          }}
        />
      </section>

      {/* This wrapper is required for the sticky navbar to work on safary lol */}
      <div>
        <OrSecondaryNavbarBdp
          observerRefs={observerRefs}
          links={pageLinks.map((link) => {
            return {
              ...link,
              onTrackClick: (event?: MouseEvent<HTMLAnchorElement>) => {
                event?.stopPropagation()
                trackNavigationMenuClick({
                  menuTitle: link.label?.toLocaleLowerCase() || '',
                  menuHeading: link.label?.toLocaleLowerCase() || '',
                  menuType: 'secondary navigation',
                  linkUrl: link.href || '',
                  componentName: 'secondary nav bar bdp',
                  windowLocationHref: window.location.href,
                })
              },
            }
          })}
        />

        {location && (
          <section
            id={location.mapTile.id}
            className="mt-8 px-4 lg:px-0 lg:max-w-screen-lg lg:mx-auto scroll-mt-24"
            ref={(el) => {
              observerRefs.current[location.mapTile.id] = el
            }}
          >
            <PortableText
              value={location.heading}
              components={{
                block: {
                  h2: ({ children }) => (
                    <h2 className="font-heading font-medium text-32 leading-1.3 pb-4">{children}</h2>
                  ),
                  normal: ({ children }) => <p className="text-neutrals-700 text-14 lg:text-16">{children}</p>,
                },
              }}
            />

            <div className="flex flex-col gap-4 slg:gap-12 slg:flex-row mt-4 mb-10">
              <PortableText
                value={location.addressInfo}
                components={{
                  block: {
                    normal: ({ children }) => <p className="text-neutrals-700 text-14 lg:text-16">{children}</p>,
                    strong: ({ children }) => (
                      <strong className="only:inline-block first:inline-block first:mt-6 only:mt-6 font-bold text-neutrals-800 leading-1.5">
                        {children}
                      </strong>
                    ),
                  },
                }}
              />
            </div>

            {location.mapTile && (
              <OrMapTile
                apiKey={googleMapsApiKey}
                markers={[{ address: location.mapTile.address }]}
                details={location.mapTile.details.items.map((item) => ({
                  icon: item.icon?.src,
                  label: item.title,
                }))}
              />
            )}
          </section>
        )}

        <hr className="block slg:hidden border mt-10 border-neutrals-400" />

        {content?.map((item, index) => (
          <ContentBlock
            key={index}
            content={{ ...item, images: item.images?.map((image) => mapImage(image, { width: 705 })) }}
            observerRefs={observerRefs}
          />
        ))}
      </div>
    </div>
  )
}
